import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Clock, CaretLeft, Funnel, ArrowLeft, BookOpenText, Sparkle } from "@phosphor-icons/react";
import { ARTICLES, ARTICLE_CATEGORIES } from "../../data/articles/articlesData";
import usePageSeo from "../../hooks/usePageSeo";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import "./ArticlesPage.css";

function ArticlesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfession, setSelectedProfession] = useState("all");
  const [selectedTopic, setSelectedTopic] = useState("all");

  usePageSeo({
    title: "מאמרים | CliniKit – תוכנה לניהול קליניקה למטפלים",
    description: "מאמרים ומדריכים בנושא ניהול קליניקה, תוכנה למטפלים, טכנולוגיה בטיפול ועוד. CliniKit – מערכת ניהול קליניקה למטפלים.",
    path: "/blog",
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "CliniKit Blog",
      url: "https://clinikit.co.il/blog",
      description: "מאמרים ומדריכים בנושא ניהול קליניקה למטפלים",
      publisher: {
        "@type": "Organization",
        name: "CliniKit",
        alternateName: "קליניקיט",
        url: "https://clinikit.co.il",
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-jsonld";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("blog-jsonld");
      if (el) el.remove();
    };
  }, []);

  const filteredArticles = useMemo(() => {
    return ARTICLES.filter((article) => {
      const matchProfession =
        selectedProfession === "all" || article.profession === selectedProfession;
      const matchTopic =
        selectedTopic === "all" || article.topic === selectedTopic;
      const matchSearch =
        !searchQuery.trim() ||
        article.title.includes(searchQuery) ||
        article.description.includes(searchQuery) ||
        article.keywords.some((kw) => kw.includes(searchQuery));
      return matchProfession && matchTopic && matchSearch;
    });
  }, [selectedProfession, selectedTopic, searchQuery]);

  const hasActiveFilters =
    selectedProfession !== "all" || selectedTopic !== "all" || searchQuery.trim();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getProfessionLabel = (value) => {
    const found = ARTICLE_CATEGORIES.professions.find((p) => p.value === value);
    return found ? found.label : value;
  };

  const getTopicLabel = (value) => {
    const found = ARTICLE_CATEGORIES.topics.find((t) => t.value === value);
    return found ? found.label : value;
  };

  return (
    <div className="articles-page">
      <Header />

      <main>
        {/* Hero */}
        <section className="articles-hero">
          <div className="articles-hero-bg">
            <div className="articles-hero-orb articles-hero-orb-1" />
            <div className="articles-hero-orb articles-hero-orb-2" />
            <div className="articles-hero-orb articles-hero-orb-3" />
          </div>

          <div className="container">
            {/* Breadcrumb */}
            <nav className="articles-breadcrumb" aria-label="Breadcrumb">
              <ol itemScope itemType="https://schema.org/BreadcrumbList">
                <li
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                >
                  <Link to="/" itemProp="item">
                    <span itemProp="name">דף הבית</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                  <CaretLeft size={14} weight="bold" />
                </li>
                <li
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                  aria-current="page"
                >
                  <span itemProp="name">מאמרים</span>
                  <meta itemProp="position" content="2" />
                </li>
              </ol>
            </nav>

            <div className="articles-hero-badge">
              <BookOpenText size={18} weight="duotone" />
              <span>הבלוג של CliniKit</span>
            </div>
            <h1>מאמרים ומדריכים <span className="articles-hero-highlight">למטפלים</span></h1>
            <p className="articles-hero-subtitle">
              מדריכים מקצועיים, טיפים מעשיים ומאמרים מעמיקים בנושאי ניהול קליניקה, טכנולוגיה בטיפול, שיווק למטפלים ועוד
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="articles-filters">
          <div className="container">
            <div className="articles-filters-bar">
              <div className="articles-search">
                <MagnifyingGlass size={20} weight="regular" />
                <input
                  type="text"
                  placeholder="חיפוש מאמרים..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="articles-filter-divider" />

              <div className="articles-filter-selects">
                <div className="articles-filter-select">
                  <Funnel size={16} weight="duotone" />
                  <select
                    value={selectedProfession}
                    onChange={(e) => setSelectedProfession(e.target.value)}
                  >
                    {ARTICLE_CATEGORIES.professions.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="articles-filter-select">
                  <Funnel size={16} weight="duotone" />
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                  >
                    {ARTICLE_CATEGORIES.topics.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <button
                  className="articles-clear-filters-btn"
                  onClick={() => {
                    setSelectedProfession("all");
                    setSelectedTopic("all");
                    setSearchQuery("");
                  }}
                >
                  נקה הכל
                </button>
              )}
            </div>

            <div className="articles-results-count">
              <Sparkle size={16} weight="duotone" />
              <span>{filteredArticles.length} מאמרים</span>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="articles-grid-section">
          <div className="container">
            {filteredArticles.length === 0 ? (
              <div className="articles-empty">
                <div className="articles-empty-icon">
                  <BookOpenText size={48} weight="duotone" />
                </div>
                <h3>לא נמצאו מאמרים</h3>
                <p>נסו לשנות את הפילטרים או את מילות החיפוש</p>
                <button
                  className="articles-clear-filters"
                  onClick={() => {
                    setSelectedProfession("all");
                    setSelectedTopic("all");
                    setSearchQuery("");
                  }}
                >
                  נקו פילטרים
                </button>
              </div>
            ) : (
              <div className="articles-grid">
                {filteredArticles.map((article, index) => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    className={`article-card ${index === 0 ? "article-card-featured" : ""}`}
                  >
                    <div className="article-card-body">
                      <div className="article-card-tags">
                        <span className="article-tag article-tag-profession">
                          {getProfessionLabel(article.profession)}
                        </span>
                        <span className="article-tag article-tag-topic">
                          {getTopicLabel(article.topic)}
                        </span>
                      </div>

                      <h2 className="article-card-title">{article.title}</h2>
                      <p className="article-card-description">
                        {article.description}
                      </p>

                      <div className="article-card-footer">
                        <div className="article-card-meta">
                          <span className="article-card-date">
                            {formatDate(article.date)}
                          </span>
                          <span className="article-card-dot" />
                          <span className="article-card-read-time">
                            <Clock size={14} weight="regular" />
                            {article.readTime} דק׳ קריאה
                          </span>
                        </div>
                        <span className="article-card-arrow">
                          <ArrowLeft size={16} weight="bold" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="articles-cta">
          <div className="articles-cta-bg">
            <div className="articles-cta-orb articles-cta-orb-1" />
            <div className="articles-cta-orb articles-cta-orb-2" />
          </div>
          <div className="container">
            <div className="articles-cta-inner">
              <div className="articles-cta-badge">
                <Sparkle size={16} weight="fill" />
                30 ימי ניסיון חינם
              </div>
              <h2>מוכנים לנהל את הקליניקה בצורה חכמה?</h2>
              <p>הצטרפו ל-500+ מטפלים שכבר משתמשים ב-CliniKit לניהול הקליניקה שלהם</p>
              <Link to="/#lead-form" className="articles-cta-btn">
                קבעו שיחת הדרכה
                <ArrowLeft size={16} weight="bold" />
              </Link>
              <span className="articles-cta-note">ללא כרטיס אשראי · ללא התחייבות</span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ArticlesPage;
